/**
 * Environment Variable Service
 * Manages local storage of environment variables in IndexedDB with CSV import/export
 * Variables are stored LOCALLY only and never sent to the backend database
 * Supports auto-save/load from a persistent CSV file on user's computer
 */

import { DotEnvProfile, EnvVariable, EnvVariableScope } from '../types/envVariables';
import { apiService } from './apiService';
import { normalizeEnvVariableScope } from '../utils/executionEnvironment';
import { validateEnvironmentBaseUrl } from '../utils/environmentBaseUrlValidation';

const DB_NAME = 'crud-expert-env-variables';
/** Must never be lower than any version already created for this DB name in users' browsers. */
const DB_VERSION = 2;
const STORE_NAME = 'env-variables';
const FILE_HANDLE_KEY = 'env-variables-file-handle';
const SCOPED_KEY_PREFIX = (projectId: number) => `env_${projectId}::`;
const SCOPED_KEY_PREFIX_RE = /^env_\d+::/;
const SCOPED_KEY_WITH_SCOPE_RE = /^env_(\d+)::(environment|project)::(.*)$/;
const SCOPED_KEY_LEGACY_RE = /^env_(\d+)::(.*)$/;
const DOTENV_PROFILE_MARKER = '__dotenv_profile__';
const makeProfileKey = (projectId: number): string => `${SCOPED_KEY_PREFIX(projectId)}${DOTENV_PROFILE_MARKER}`;

const isDotEnvProfileRecordKey = (key: string): boolean =>
  typeof key === 'string' && key.endsWith(`::${DOTENV_PROFILE_MARKER}`);

const makeScopedKey = (
  projectId: number,
  key: string,
  scope: EnvVariableScope = 'environment'
): string => `${SCOPED_KEY_PREFIX(projectId)}${scope}::${key}`;

function parseStoredEnvRecord(rawKey: string, record: any): EnvVariable {
  const withScope = rawKey.match(SCOPED_KEY_WITH_SCOPE_RE);
  if (withScope) {
    return {
      ...record,
      key: withScope[3],
      scope: normalizeEnvVariableScope(withScope[2]),
    } as EnvVariable;
  }
  const legacy = rawKey.match(SCOPED_KEY_LEGACY_RE);
  if (legacy) {
    return {
      ...record,
      key: legacy[2],
      scope: normalizeEnvVariableScope(record?.scope),
    } as EnvVariable;
  }
  return {
    ...record,
    key: rawKey,
    scope: normalizeEnvVariableScope(record?.scope),
  } as EnvVariable;
}

// Check if File System Access API is supported
const isFileSystemAccessSupported = (): boolean => {
  return 'showOpenFilePicker' in window && 'showSaveFilePicker' in window;
};

/**
 * Initialize IndexedDB. Handles VersionError when the on-disk DB was upgraded elsewhere
 * (e.g. another machine or older build used a higher open() version than this bundle).
 */
const initDB = (): Promise<IDBDatabase> => {
  const attachHandlers = (
    req: IDBOpenDBRequest,
    resolve: (db: IDBDatabase) => void,
    reject: (err: DOMException | null) => void
  ) => {
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        objectStore.createIndex('key', 'key', { unique: true });
      }
    };
  };

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    attachHandlers(request, resolve, (err) => {
      if (err?.name === 'VersionError') {
        const fallback = indexedDB.open(DB_NAME);
        attachHandlers(fallback, resolve, reject);
        return;
      }
      reject(err);
    });
  });
};

/**
 * Get all environment variables from IndexedDB
 */
export const getAllEnvVariables = async (projectId?: number): Promise<EnvVariable[]> => {
  return getAllEnvVariablesScoped(projectId);
};

/**
 * Internal helper: shape/filter values returned from IndexedDB.
 */
const normalizeEnvVariableRecords = (records: any[], projectId?: number): EnvVariable[] => {
  const variablesOnly = (records || []).filter(
    (r) =>
      r &&
      typeof r.key === 'string' &&
      r.key !== FILE_HANDLE_KEY &&
      !isDotEnvProfileRecordKey(r.key) &&
      r.value !== undefined &&
      r.value !== null
  );

  if (projectId == null) {
    return variablesOnly.map((v) => parseStoredEnvRecord(v.key, v));
  }

  const prefix = SCOPED_KEY_PREFIX(projectId);
  const scoped = variablesOnly
    .filter((v) => typeof v.key === 'string' && v.key.startsWith(prefix))
    .map((v) => parseStoredEnvRecord(v.key, v));

  const scopedIdentities = new Set(scoped.map((v) => `${normalizeEnvVariableScope(v.scope)}:${v.key}`));
  const legacy = variablesOnly
    .filter((v) => typeof v.key === 'string' && !SCOPED_KEY_PREFIX_RE.test(v.key))
    .filter((v) => v.key)
    .map((v) => parseStoredEnvRecord(v.key, v));

  return [
    ...scoped,
    ...legacy.filter((l) => !scopedIdentities.has(`${normalizeEnvVariableScope(l.scope)}:${l.key}`)),
  ];
};

// Re-define with actual logic (keeping export stable for callers).
export const getAllEnvVariablesScoped = async (projectId?: number): Promise<EnvVariable[]> => {
  const db = await initDB();
  return new Promise<EnvVariable[]>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      // `IDBRequest.result` is typed as `unknown` under strict DOM typings.
      // We store env-variable-like records in this store, so treat unknown as an array here.
      const records = (request.result as any[] | undefined) ?? [];
      resolve(normalizeEnvVariableRecords(records, projectId));
    };
    request.onerror = () => reject(request.error);
  }).then(async (merged: EnvVariable[]) => {
    // Best-effort migration of legacy unscoped keys into the current project.
    if (projectId == null) return merged;
    const db2 = await initDB();
    return new Promise<EnvVariable[]>((resolve, reject) => {
      const transaction = db2.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const getReq = store.getAll();
      getReq.onsuccess = () => {
        const records = (getReq.result as any[] | undefined) ?? [];

        const scopedPrefix = SCOPED_KEY_PREFIX(projectId);
        const scopedIdentities = new Set(
          records
            .filter(
              (r) =>
                r &&
                typeof r.key === 'string' &&
                r.key.startsWith(scopedPrefix) &&
                !isDotEnvProfileRecordKey(r.key)
            )
            .map((r) => {
              const parsed = parseStoredEnvRecord(r.key, r);
              return `${normalizeEnvVariableScope(parsed.scope)}:${parsed.key}`;
            })
        );

        records.forEach((r) => {
          if (!r || typeof r.key !== 'string' || !r.key.startsWith(scopedPrefix)) return;
          if (isDotEnvProfileRecordKey(r.key)) return;
          if (SCOPED_KEY_WITH_SCOPE_RE.test(r.key)) return;
          if (!SCOPED_KEY_LEGACY_RE.test(r.key)) return;
          const parsed = parseStoredEnvRecord(r.key, r);
          const targetKey = makeScopedKey(projectId, parsed.key, normalizeEnvVariableScope(parsed.scope));
          store.put({ ...r, key: targetKey, scope: normalizeEnvVariableScope(parsed.scope) });
          store.delete(r.key);
        });

        const legacyRecords = records.filter(
          (r) =>
            r &&
            typeof r.key === 'string' &&
            r.key !== FILE_HANDLE_KEY &&
            !SCOPED_KEY_PREFIX_RE.test(r.key) &&
            r.value !== undefined &&
            r.value !== null
        );

        legacyRecords.forEach((legacy) => {
          const rawKey = legacy.key as string;
          const parsed = parseStoredEnvRecord(rawKey, legacy);
          const identity = `${normalizeEnvVariableScope(parsed.scope)}:${parsed.key}`;
          const targetKey = makeScopedKey(projectId, parsed.key, normalizeEnvVariableScope(parsed.scope));

          if (scopedIdentities.has(identity)) {
            store.delete(rawKey);
          } else {
            store.put({ ...legacy, key: targetKey, scope: normalizeEnvVariableScope(parsed.scope) });
            store.delete(rawKey);
          }
        });
      };
      getReq.onerror = () => reject(getReq.error);

      transaction.oncomplete = () => resolve(merged);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  });
};

/**
 * Load the per-project .Env profile (base URL and metadata).
 */
export const getDotEnvProfile = async (projectId: number): Promise<DotEnvProfile | null> => {
  const db = await initDB();
  const storageKey = makeProfileKey(projectId);

  const readRecord = (key: string): Promise<{ baseUrl?: string; updatedAt?: string } | undefined> =>
    new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result as { baseUrl?: string; updatedAt?: string } | undefined);
      request.onerror = () => reject(request.error);
    });

  const toProfile = (record: { baseUrl?: string; updatedAt?: string } | undefined): DotEnvProfile | null => {
    const baseUrl = String(record?.baseUrl ?? '').trim();
    if (!record || !baseUrl) return null;
    return {
      baseUrl,
      updatedAt: record.updatedAt || new Date().toISOString(),
    };
  };

  const canonical = toProfile(await readRecord(storageKey));
  if (canonical) return canonical;

  // Recover profile records moved by an older migration pass.
  const migratedKey = makeScopedKey(projectId, DOTENV_PROFILE_MARKER, 'environment');
  const migrated = toProfile(await readRecord(migratedKey));
  if (!migrated) return null;

  await saveDotEnvProfile(projectId, migrated.baseUrl);
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete(migratedKey);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  return migrated;
};

/**
 * Save the per-project .Env profile base URL.
 */
export const saveDotEnvProfile = async (projectId: number, baseUrl: string): Promise<void> => {
  const trimmed = String(baseUrl ?? '').trim();
  const urlError = validateEnvironmentBaseUrl(trimmed);
  if (urlError) {
    throw new Error(urlError);
  }

  const db = await initDB();
  const storageKey = makeProfileKey(projectId);
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put({
      key: storageKey,
      baseUrl: trimmed,
      updatedAt: new Date().toISOString(),
    });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Add or update an environment variable
 */
export const saveEnvVariable = async (projectId: number | undefined, variable: EnvVariable): Promise<void> => {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const varToSave = {
      ...variable,
      scope: normalizeEnvVariableScope(variable.scope),
      updatedAt: new Date().toISOString(),
      createdAt: variable.createdAt || new Date().toISOString(),
      key:
        projectId == null
          ? variable.key
          : makeScopedKey(projectId, variable.key, normalizeEnvVariableScope(variable.scope)),
    };

    const request = store.put(varToSave);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete an environment variable
 */
export const deleteEnvVariable = async (
  projectId: number | undefined,
  key: string,
  scope?: string | null
): Promise<void> => {
  const db = await initDB();
  const storageKey =
    projectId == null ? key : makeScopedKey(projectId, key, normalizeEnvVariableScope(scope));
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(storageKey);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all environment variables (but preserve file handle)
 */
export const clearAllEnvVariables = async (projectId?: number): Promise<void> => {
  const db = await initDB();
  
  // First, save the file handle if it exists
  const fileHandle = await getFileHandle();
  
  if (projectId == null) {
    return new Promise(async (resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = async () => {
        // Restore the file handle after clearing
        if (fileHandle) {
          console.log('[clearAllEnvVariables] Restoring file handle after clear...');
          await saveFileHandle(fileHandle);
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  const prefix = SCOPED_KEY_PREFIX(projectId);
  return new Promise(async (resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = async () => {
      const records = request.result || [];
      records.forEach((r: any) => {
        if (!r || typeof r.key !== 'string') return;
        if (r.key === FILE_HANDLE_KEY) return; // preserve file handle
        const key: string = r.key;
        // Delete scoped keys for this project, plus legacy unscoped keys.
        const isScopedForProject = key.startsWith(prefix);
        const isLegacy = !SCOPED_KEY_PREFIX_RE.test(key);
        if (isScopedForProject || isLegacy) {
          store.delete(key);
        }
      });
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Export environment variables to CSV format
 */
export const exportToCSV = async (projectId?: number): Promise<string> => {
  const allItems = await getAllEnvVariablesScoped(projectId);
  
  // Filter out the file handle - only export actual environment variables
  const variables = allItems.filter(v => v.key && v.key !== FILE_HANDLE_KEY);
  
  console.log('[exportToCSV] Exporting', variables.length, 'variables (filtered out file handle)');

  const profile = projectId != null ? await getDotEnvProfile(projectId) : null;
  const baseUrl = profile?.baseUrl ?? '';

  let csv = `baseUrl,${escapeCSV(baseUrl)}\n`;
  csv += 'key,value,scope,description,createdAt,updatedAt\n';
  
  // Add rows
  variables.forEach(v => {
    const key = escapeCSV(v.key);
    const value = escapeCSV(v.value);
    const scope = escapeCSV(normalizeEnvVariableScope(v.scope));
    const description = escapeCSV(v.description || '');
    const createdAt = escapeCSV(v.createdAt);
    const updatedAt = escapeCSV(v.updatedAt);
    
    csv += `${key},${value},${scope},${description},${createdAt},${updatedAt}\n`;
  });
  
  return csv;
};

/**
 * Download CSV file to user's computer
 */
export const downloadCSV = async (filename: string = 'env-variables.csv', projectId?: number): Promise<void> => {
  const csv = await exportToCSV(projectId);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Import environment variables from CSV content
 */
export const importFromCSV = async (
  csvContent: string,
  replaceExisting: boolean = false,
  projectId?: number
): Promise<{ success: number; errors: string[]; baseUrl?: string }> => {
  console.log('[importFromCSV] Starting import...');
  console.log('[importFromCSV] CSV content:', csvContent);
  console.log('[importFromCSV] Replace existing:', replaceExisting);
  
  const lines = csvContent.split('\n').filter(line => line.trim());
  console.log('[importFromCSV] Lines after split and filter:', lines);
  console.log('[importFromCSV] Number of lines:', lines.length);
  
  const errors: string[] = [];
  let successCount = 0;

  if (lines.length === 0) {
    errors.push('CSV file is empty');
    console.log('[importFromCSV] CSV file is empty');
    return { success: 0, errors };
  }

  let headerLineIndex = 0;
  let importedBaseUrl: string | undefined;
  for (let i = 0; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i]).map((c) => c.trim());
    const first = (cells[0] || '').toLowerCase();
    if (first === 'baseurl') {
      importedBaseUrl = String(cells[1] ?? '').trim();
      continue;
    }
    if (first === 'key' && (cells[1] || '').toLowerCase() === 'value') {
      headerLineIndex = i;
      break;
    }
    if (i === 0 && first === 'key') {
      headerLineIndex = 0;
      break;
    }
  }

  const headerCells = parseCSVLine(lines[headerLineIndex]).map((h) => h.trim().toLowerCase());
  const hasScopeColumn = headerCells.includes('scope');
  const dataLines = lines.slice(headerLineIndex + 1);

  if (importedBaseUrl !== undefined && projectId != null) {
    const urlError = validateEnvironmentBaseUrl(importedBaseUrl);
    if (urlError) {
      errors.push(`Invalid baseUrl in CSV: ${urlError}`);
      return { success: 0, errors };
    }
  }

  const parseImportRow = (values: string[]): { key: string; value: string; scope: EnvVariableScope; description: string; createdAt: string; updatedAt: string } => {
    const rawKey = (values[0] || '').trim();
    const value = String(values[1] ?? '').trim();
    if (hasScopeColumn) {
      return {
        key: rawKey,
        value,
        scope: normalizeEnvVariableScope(values[2]),
        description: (values[3] || '').trim(),
        createdAt: (values[4] || '').trim(),
        updatedAt: (values[5] || '').trim(),
      };
    }
    return {
      key: rawKey,
      value,
      scope: 'environment',
      description: (values[2] || '').trim(),
      createdAt: (values[3] || '').trim(),
      updatedAt: (values[4] || '').trim(),
    };
  };

  // Load existing variables (for duplicate detection when not replacing)
  let existingKeysNormalized = new Set<string>();
  if (!replaceExisting) {
    try {
      const existingVars = await getAllEnvVariablesScoped(projectId);
      existingKeysNormalized = new Set(
        existingVars
          .filter(v => v.key && v.key !== FILE_HANDLE_KEY)
          .map(v => `${normalizeEnvVariableScope(v.scope)}:${v.key.trim()}`)
      );
    } catch (e) {
      console.error('[importFromCSV] Failed to load existing variables for duplicate check:', e);
    }
  }

  // Detect duplicate keys in CSV before importing (exact key match within the CSV itself,
  // and against existing keys when not replacing)
  const keysSeen = new Map<string, number>(); // normalizedKey -> first line number
  const duplicateKeys: string[] = [];
  const conflictsWithExisting: string[] = [];
  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i].trim();
    if (!line) continue;
    try {
      const values = parseCSVLine(line);
      if (values.length >= 2) {
        const row = parseImportRow(values);
        if (row.key) {
          const identity = `${row.scope}:${normalizeKeyForImport(row.key)}`;
          const firstLine = keysSeen.get(identity);
          if (firstLine !== undefined) {
            if (!duplicateKeys.includes(row.key)) {
              duplicateKeys.push(row.key);
            }
          } else {
            keysSeen.set(identity, i + 2);
          }

          if (!replaceExisting && existingKeysNormalized.has(identity)) {
            if (!conflictsWithExisting.includes(row.key)) {
              conflictsWithExisting.push(row.key);
            }
          }
        }
      }
    } catch {
      // Skip malformed lines during duplicate check
    }
  }
  if (duplicateKeys.length > 0) {
    errors.push(
      'Duplicate variable keys detected in the CSV file: ' +
        duplicateKeys.join(', ')
    );
    return { success: 0, errors };
  }

  if (conflictsWithExisting.length > 0) {
    errors.push(
      'CSV contains keys that already exist in your environment variables: ' +
        conflictsWithExisting.join(', ')
    );
    return { success: 0, errors };
  }

  // Clear existing if replace mode
  if (replaceExisting) {
    console.log('[importFromCSV] Clearing existing variables...');
    await clearAllEnvVariables(projectId);
  }

  if (importedBaseUrl !== undefined && projectId != null) {
    await saveDotEnvProfile(projectId, importedBaseUrl);
  }

  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i].trim();
    if (!line) continue;

    try {
      const values = parseCSVLine(line);
      console.log(`[importFromCSV] Line ${i + 2} parsed values:`, values);
      
      if (values.length < 2) {
        errors.push(`Line ${i + 2}: Invalid format - need at least key and value`);
        continue;
      }

      const row = parseImportRow(values);
      const key = normalizeKeyForImport(row.key);

      if (!key || !row.value) {
        errors.push(`Line ${i + 2}: Key and value are required`);
        continue;
      }

      const variable: EnvVariable = {
        key,
        value: row.value,
        scope: row.scope,
        description: row.description || '',
        createdAt: row.createdAt || new Date().toISOString(),
        updatedAt: row.updatedAt || new Date().toISOString(),
      };

      console.log(`[importFromCSV] Saving variable:`, variable);
      await saveEnvVariable(projectId, variable);
      successCount++;
    } catch (error: any) {
      console.error(`[importFromCSV] Error on line ${i + 2}:`, error);
      errors.push(`Line ${i + 2}: ${error.message}`);
    }
  }

  console.log('[importFromCSV] Import complete. Success:', successCount, 'Errors:', errors.length);
  return { success: successCount, errors, baseUrl: importedBaseUrl };
};

/**
 * Parse CSV file from File object
 */
export const parseCSVFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content);
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Validate environment variable key.
 * Aligned with CSV import: any non-empty key is accepted (including special characters).
 */
export const validateKey = (key: string): { valid: boolean; error?: string } => {
  if (!key || key.trim().length === 0) {
    return { valid: false, error: 'Key is required' };
  }

  return { valid: true };
};

/**
 * Validate environment variable value
 */
export const validateValue = (value: string): { valid: boolean; error?: string } => {
  if (value === undefined || value === null) {
    return { valid: false, error: 'Value is required' };
  }

  return { valid: true };
};

/**
 * Escape CSV field
 */
const escapeCSV = (field: string): string => {
  if (!field) return '';
  
  // If field contains comma, quote, or newline, wrap in quotes and escape quotes
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  
  return field;
};

/**
 * Parse a CSV line handling quoted fields
 */
const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  // Add last field
  result.push(current);

  return result;
};

const normalizeKeyForImport = (rawKey: string): string => {
  return (rawKey || '').trim();
};

/**
 * Get environment variable by key
 */
export const getEnvVariableByKey = async (
  projectId: number | undefined,
  key: string,
  scope?: string | null
): Promise<EnvVariable | undefined> => {
  const db = await initDB();
  const scopedKey =
    projectId == null ? key : makeScopedKey(projectId, key, normalizeEnvVariableScope(scope));
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(scopedKey);

    request.onsuccess = () => {
      const row = request.result;
      resolve(row ? parseStoredEnvRecord(row.key, row) : undefined);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Check if key exists
 */
export const keyExists = async (
  projectId: number | undefined,
  key: string,
  scope?: string | null
): Promise<boolean> => {
  const variable = await getEnvVariableByKey(projectId, key, scope);
  return variable !== undefined;
};

/**
 * Get saved file path from database
 */
export const getSavedFilePath = async (): Promise<string | null> => {
  try {
    const response = await apiService.get('/user-settings/env-csv-path');
    const path = response.data.envCsvFilePath || null;
    console.log('[getSavedFilePath] Path from database:', path);
    return path;
  } catch (error: any) {
    console.error('[getSavedFilePath] Error:', error);
    return null;
  }
};

/**
 * Save file path to database
 */
export const saveFilePath = async (path: string): Promise<void> => {
  try {
    console.log('[saveFilePath] Saving path to database:', path);
    await apiService.post('/user-settings/env-csv-path', { envCsvFilePath: path });
    console.log('[saveFilePath] Path saved successfully');
  } catch (error: any) {
    console.error('[saveFilePath] Error:', error);
  }
};

/**
 * Clear file path from database
 */
export const clearFilePath = async (): Promise<void> => {
  try {
    console.log('[clearFilePath] Clearing path from database');
    await apiService.delete('/user-settings/env-csv-path');
    console.log('[clearFilePath] Path cleared successfully');
  } catch (error: any) {
    console.error('[clearFilePath] Error:', error);
  }
};

/**
 * Save file handle to IndexedDB (for File System Access API)
 */
export const saveFileHandle = async (handle: FileSystemFileHandle): Promise<void> => {
  if (!isFileSystemAccessSupported()) return;
  
  try {
    console.log('[saveFileHandle] Saving file handle:', handle.name);
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put({ key: FILE_HANDLE_KEY, handle });

      request.onsuccess = () => {
        console.log('[saveFileHandle] ✅ File handle saved successfully');
        resolve();
      };
      request.onerror = () => {
        console.error('[saveFileHandle] ❌ Failed to save:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[saveFileHandle] Exception:', error);
  }
};

/**
 * Get saved file handle from IndexedDB
 */
export const getFileHandle = async (): Promise<FileSystemFileHandle | null> => {
  if (!isFileSystemAccessSupported()) {
    console.log('[getFileHandle] File System Access API not supported');
    return null;
  }
  
  try {
    console.log('[getFileHandle] Retrieving file handle...');
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      // First, let's see all items in the store for debugging
      const getAllRequest = store.getAll();
      getAllRequest.onsuccess = () => {
        console.log('[getFileHandle] All items in IndexedDB store:', getAllRequest.result);
      };
      
      const request = store.get(FILE_HANDLE_KEY);

      request.onsuccess = () => {
        const result = request.result;
        console.log('[getFileHandle] Result for FILE_HANDLE_KEY:', result);
        console.log('[getFileHandle] FILE_HANDLE_KEY value:', FILE_HANDLE_KEY);
        const handle = result?.handle || null;
        if (handle) {
          console.log('[getFileHandle] ✅ File handle found:', handle.name);
        } else {
          console.log('[getFileHandle] ❌ No file handle found in IndexedDB');
        }
        resolve(handle);
      };
      request.onerror = () => {
        console.error('[getFileHandle] Error:', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('[getFileHandle] Exception:', error);
    return null;
  }
};

/**
 * Clear saved file handle
 */
export const clearFileHandle = async (): Promise<void> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(FILE_HANDLE_KEY);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to clear file handle:', error);
  }
};

/**
 * Auto-save variables to the saved file location
 */
export const autoSaveToFile = async (projectId?: number): Promise<{ success: boolean; error?: string }> => {
  if (!isFileSystemAccessSupported()) {
    console.warn('[autoSaveToFile] File System Access API not supported');
    return { success: false, error: 'File System Access API not supported' };
  }

  try {
    console.log('[autoSaveToFile] Starting auto-save...');
    const handle = await getFileHandle();
    if (!handle) {
      console.warn('[autoSaveToFile] No file handle found');
      return { success: false, error: 'No file location set' };
    }

    console.log('[autoSaveToFile] File handle retrieved, checking permission...');
    // Verify we still have permission (cast to any to avoid TypeScript issues)
    const handleAny = handle as any;
    const permission = await handleAny.queryPermission({ mode: 'readwrite' });
    console.log('[autoSaveToFile] Current permission:', permission);
    
    if (permission !== 'granted') {
      console.log('[autoSaveToFile] Requesting permission...');
      const newPermission = await handleAny.requestPermission({ mode: 'readwrite' });
      console.log('[autoSaveToFile] New permission:', newPermission);
      if (newPermission !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }
    }

    // Get all variables and export to CSV
    console.log('[autoSaveToFile] Exporting variables to CSV...');
    const csv = await exportToCSV(projectId);
    console.log('[autoSaveToFile] CSV content length:', csv.length);
    
    // Write to file (cast to any to avoid TypeScript issues)
    console.log('[autoSaveToFile] Creating writable stream...');
    const writable = await handleAny.createWritable();
    console.log('[autoSaveToFile] Writing to file...');
    await writable.write(csv);
    await writable.close();

    console.log('[autoSaveToFile] ✅ Auto-save successful!');
    return { success: true };
  } catch (error: any) {
    console.error('[autoSaveToFile] ❌ Auto-save failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Auto-load variables from the saved file location
 */
export const autoLoadFromFile = async (projectId?: number): Promise<{ success: boolean; count?: number; error?: string }> => {
  if (!isFileSystemAccessSupported()) {
    return { success: false, error: 'File System Access API not supported' };
  }

  try {
    const handle = await getFileHandle();
    if (!handle) {
      return { success: false, error: 'No file location set' };
    }

    // Verify we still have permission (cast to any to avoid TypeScript issues)
    const handleAny = handle as any;
    const permission = await handleAny.queryPermission({ mode: 'read' });
    if (permission !== 'granted') {
      const newPermission = await handleAny.requestPermission({ mode: 'read' });
      if (newPermission !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }
    }

    // Read file
    const file = await handle.getFile();
    const content = await file.text();
    console.log('[autoLoadFromFile] File content:', content);
    console.log('[autoLoadFromFile] File content length:', content.length);
    
    // Import CSV
    const result = await importFromCSV(content, true, projectId); // Replace existing
    console.log('[autoLoadFromFile] Import result:', result);
    
    console.log(`Auto-loaded ${result.success} env variables from file`);
    return { success: true, count: result.success };
  } catch (error: any) {
    console.error('Auto-load failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Select and save a CSV file location (existing file only via Open picker).
 */
export const selectFileLocation = async (): Promise<{ success: boolean; path?: string; error?: string }> => {
  if (!isFileSystemAccessSupported()) {
    return { success: false, error: 'File System Access API not supported in this browser' };
  }

  try {
    console.log('[selectFileLocation] Opening file picker...');
    
    // Try to open an existing file first
    let handle: FileSystemFileHandle;
    
    try {
      // Show open file picker — CSV only (single dialog; do not chain Save after cancel — that felt like "Cancel opens again")
      const handles = await (window as any).showOpenFilePicker({
        types: [{
          description: 'CSV Files (.csv only)',
          accept: { 'text/csv': ['.csv'] },
        }],
        multiple: false,
      });
      handle = handles[0];
      if (!handle.name.toLowerCase().endsWith('.csv')) {
        return { success: false, error: 'Only .csv files can be linked. Please select a CSV file.' };
      }
      console.log('[selectFileLocation] Existing file selected:', handle.name);
    } catch (openError: any) {
      if (openError.name === 'AbortError') {
        console.log('[selectFileLocation] File selection cancelled');
        return { success: false, error: 'File selection cancelled' };
      }
      throw openError;
    }

    console.log('[selectFileLocation] File selected (CSV only):', handle.name);
    await saveFileHandle(handle);
    console.log('[selectFileLocation] File handle saved');
    const path = handle.name;
    saveFilePath(path);
    console.log('[selectFileLocation] File path saved');

    // CSV is never written automatically — only Export / Import / Reload touch the file
    return { success: true, path };
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.log('[selectFileLocation] File selection cancelled');
      return { success: false, error: 'File selection cancelled' };
    }
    console.error('[selectFileLocation] Error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if auto-save/load is available
 */
export const isAutoSaveAvailable = (): boolean => {
  return isFileSystemAccessSupported();
};
